import { ReadUser, AppError } from "esa-core";
import { SWRConfig } from "esa-hooks";
import { createContext, useContext, useMemo, useRef, type JSX } from "react";
import { Navigate, Outlet, useNavigate } from "react-router";
import urls from "const/urls";
import filterAvailableUsers from "logic/filterAvailableUsers";
import { isIpRestrictionError } from "logic/isIpRestrictionError";
import IpRestrictionPage from "pages/IpRestrictionPage";
import {
  useLoginContext,
  useLoginContextMutate,
} from "routes/protections/RequireLoginContext";

const Context = createContext<ReadUser | null>(null);

const ERROR_RETRY_COUNT = 3;

export const RequireCurrentUser = (): JSX.Element => {
  const loginContext = useLoginContext();
  const mutateLoginContext = useLoginContextMutate();
  const navigate = useNavigate();

  const onClickReturnToLogin = (): void => {
    void navigate(urls.logout);
  };

  // 画面を開いたまま IP アドレス制限が変更されると、判定の元にしている
  // login_context が古くなる。esa-api に拒否されたら取り直す
  const isRevalidating = useRef(false);
  const swrConfig = useMemo(
    () => ({
      // 取り直しても拒否され続ける場合に onError が無制限に繰り返されないよう、
      // 配下の取得のリトライに上限を設ける
      errorRetryCount: ERROR_RETRY_COUNT,
      onError: (error: unknown): void => {
        if (!isIpRestrictionError(error) || isRevalidating.current) {
          return;
        }
        isRevalidating.current = true;
        void mutateLoginContext().finally(() => {
          isRevalidating.current = false;
        });
      },
    }),
    [mutateLoginContext],
  );

  const users = filterAvailableUsers(loginContext.users);

  // ユーザが存在して currentUser が要求される場合、一度 logout して currentUser を選択させる
  if (users.length > 0 && loginContext.currentUserUid === null) {
    return <Navigate to={urls.logout} />;
  }

  const currentUser = users.find(
    (user) => user.userUid === loginContext.currentUserUid,
  );

  // ユーザが存在しない場合は、初期登録フローに流す
  if (!currentUser) {
    return <Navigate to={urls.register} />;
  }

  // IP アドレス制限。多要素認証による免除の可否は法人ごとの設定で変わり、
  // esa-api が同じ条件で API を拒否するため判定はサーバに任せる。
  // clientIp は表示に必要なため確認する（サーバも取得できない場合は制限しない）
  if (
    loginContext.clientIp != null &&
    loginContext.restrictedUserUids.includes(currentUser.userUid)
  ) {
    return (
      <IpRestrictionPage
        clientIp={loginContext.clientIp}
        onClickReturnToLogin={onClickReturnToLogin}
      />
    );
  }

  return (
    <Context.Provider value={currentUser}>
      <SWRConfig value={swrConfig}>
        <Outlet />
      </SWRConfig>
    </Context.Provider>
  );
};

export const useCurrentUser = (): ReadUser => {
  const currentUser = useContext(Context);

  if (!currentUser) {
    throw new AppError(
      "useCurrentUser must be called inside `<RequireCurrentUser>`",
      {},
    );
  }

  return currentUser;
};
